import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { Badge, Card, PageHeader, Skeleton, Tabs } from '../../components/ui';
import { useSetCrumbs } from '../../lib/crumbs';
import { reportClientError } from '../../lib/errors';
import { ruDateTime } from '../../lib/format';
import { useRole } from '../../lib/role';
import type { AuthedPageProps } from '../../page-route';
import { ComputersTab } from './Computers';
import { HistoryView } from './HistoryView';
import { PermissionForm } from './PermissionForm';
import { SamplesLog } from './SamplesLog';
import { type AgentDataView, agentData } from './api';
import { Forbidden, LoadError, SCREEN, isForbidden, shouldReport } from './parts';
import { LABEL, T, TAB, TITLE, say } from './texts';

/*
 * «Данные для ИИ-агентов» `/admin/agent-data` (005, T139; spec.md FR-026, FR-028, US7 сценарии 3–4;
 * contracts/http-api.md). Страница администратора системы: разрешение на выборки дней объекта, история его изменений
 * и журнал выборок; с 006 — компьютеры всех пользователей. Формы — общие схемы `@tabel/shared/schemas/agent-data`
 * (`api.ts`). Четыре вкладки:
 * - «Разрешение» — форма `PermissionForm`: включено, основание, клиенты, лимиты. Форма остаётся на странице и при
 *   другой вкладке: введённое не теряется. Ответ `PUT` — разрешение после записи: он и становится данными страницы;
 * - «История» — `HistoryView`, из того же ответа сервера;
 * - «Журнал выборок» — `SamplesLog`, свой запрос; он идёт, только когда вкладку открыли;
 * - «Компьютеры» (006, T116) — `ComputersTab`: компьютеры всех пользователей без переписки, «Отключить»; свой запрос,
 *   только когда вкладку открыли.
 * Состояния: загрузка, ошибка с кодом и «Повторить», ответ не по схеме, `403` — страница только для администратора
 * системы. Под заголовком — состояние выборок и последнее изменение (первая запись истории): когда и автор.
 */

type Tab = 'permission' | 'history' | 'log' | 'computers';
const TAB_ITEMS: readonly { value: Tab; label: string }[] = [
  { value: 'permission', label: TAB.permission },
  { value: 'history', label: TAB.history },
  { value: 'log', label: TAB.log },
  { value: 'computers', label: TAB.computers },
];
const ID = 'agent-data';

export function AdminAgentDataPage({ me }: AuthedPageProps) {
  useSetCrumbs([{ label: TITLE.page }]);
  const { role } = useRole();
  const tz = me.object.tzOffset;
  const qc = useQueryClient();
  const key = ['agent-data', role] as const;
  const q = useQuery({ queryKey: key, queryFn: agentData });
  const [tab, setTab] = useState<Tab>('permission');
  const saved = (view: AgentDataView) => qc.setQueryData(key, view);

  useEffect(() => {
    if (q.error && shouldReport(q.error)) reportClientError(SCREEN, 'load', q.error);
  }, [q.error]);

  const data = q.data;
  // История — новые сверху: первая запись — последнее изменение.
  const last = data?.history[0];
  let body: ReactNode;
  if (q.isError) {
    body = isForbidden(q.error) ? (
      <Forbidden error={q.error} />
    ) : (
      <LoadError
        title={TITLE.loadError}
        error={q.error}
        busy={q.isFetching}
        onRetry={() => void q.refetch()}
      />
    );
  } else if (!data) body = <Loading />;
  else {
    const panel = (value: Tab) => ({
      role: 'tabpanel',
      id: `${ID}-panel-${value}`,
      'aria-labelledby': `${ID}-tab-${value}`,
      hidden: tab !== value,
    });
    body = (
      <div className="space-y-5">
        <Tabs label={TITLE.tabs} value={tab} onChange={setTab} items={TAB_ITEMS} idPrefix={ID} />
        {/* Форма не снимается со страницы при другой вкладке: введённое остаётся. Новая версия с сервера — заново. */}
        <div {...panel('permission')}>
          <PermissionForm key={q.dataUpdatedAt} data={data} onSaved={saved} />
        </div>
        <div {...panel('history')}>{tab === 'history' && <HistoryView history={data.history} tz={tz} />}</div>
        <div {...panel('log')}>{tab === 'log' && <SamplesLog tz={tz} />}</div>
        <div {...panel('computers')}>{tab === 'computers' && <ComputersTab tz={tz} />}</div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[960px] space-y-6">
      <PageHeader
        title={TITLE.page}
        description={T.intro}
        meta={
          data && (
            <>
              <Badge tone={data.enabled ? 'ok' : 'neutral'} dot>
                {data.enabled ? LABEL.stateOn : LABEL.stateOff}
              </Badge>
              {last && (
                <span className="text-[12.5px] text-ink-3">
                  {say.changed(ruDateTime(last.setAt, tz), last.setBy.name || null)}
                </span>
              )}
            </>
          )
        }
      />
      {body}
    </div>
  );
}

function Loading() {
  return (
    <div aria-busy="true" aria-label={LABEL.loading} className="space-y-5">
      <Skeleton className="h-9 w-80 max-w-full" />
      <Card className="space-y-4 px-6 py-5 max-sm:px-4">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-3/5" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </Card>
    </div>
  );
}
